import { useShop } from '@/contexts/ShopContext';
import { OrderNotificationMonitor } from '@/components/commerce/OrderNotificationMonitor';
import { cn } from '@/lib/utils';
import { Outlet, ScrollRestoration, useLocation, useNavigation } from 'react-router';
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
  Sidebar as DashboardV3Sidebar,
  MobileRailDrawer,
  RailSubnavCollapsedPill,
  RailSubnavPanel,
} from '@/components/dashboard-v3/sidebar';
import { FloatingTopActions, FloatingTopActionsMobile, OfflineNotice } from '@/components/owner-ui';
import { PageTransition } from '@/components/PageTransition';
import { CommandPalette } from '@/components/CommandPalette';
import { StatusBar } from '@/components/StatusBar';
import { VelkoSplash } from '@/components/VelkoSplash';
import { AccountStatusBanner } from '@/components/billing/AccountStatusBanner';
import { ExpiryCountdownBanner } from '@/components/billing/ExpiryCountdownBanner';
import { GracePeriodSheet } from '@/components/billing/GracePeriodSheet';
import { FeaturePaywall } from '@/components/billing/FeaturePaywall';
import { ReconversionScreen } from '@/components/billing/ReconversionScreen';
import { AnnouncementBanner } from '@/components/AnnouncementBanner';
import { ChatFab } from '@/components/chat/ChatFab';
import { BottomTabBar } from '@/components/mobile/BottomTabBar';
import { ProductTourHost } from '@/components/product-tour';
import { PwaInstallBanner } from '@/components/pwa/PwaInstallBanner';
import { PwaUpdatePrompt } from '@/components/pwa/PwaUpdatePrompt';
import { AppDownloadModal } from '@/components/pwa/AppDownloadModal';
import { SubNavProvider } from '@/contexts/SubNavContext';

/**
 * Owner app shell — Phase 8 HeyGen alignment (iteration 4).
 *
 * Layout structure :
 * - Left rail (88px, sticky) : logo + module items + bottom utilities.
 * - Optional persistent sub-nav panel (256px, sticky) : shown when the user
 *   navigates inside a module with children (e.g. /sales/*).
 * - Floating top-right actions cluster (sync · search · notifs · theme).
 * - Mobile : rail becomes a drawer (burger top-left). Sub-nav renders inline
 *   inside the drawer.
 *
 * `RailSubnavPanel` emits its own 256px flex-flow spacer (with width
 * transition) alongside its fixed-positioned aside, so the content area shifts
 * naturally to the right of both rails — no overlap, even on edge cases.
 */
function CockpitLayoutInner() {
  const { pathname } = useLocation();
  const { shopRevision, isSwitching } = useShop();
  const navigation = useNavigation();
  const isNavigating = navigation.state !== 'idle';
  // The dedicated chat page already is the assistant — the floating launcher
  // would overlap its composer, so hide it there.
  const isOrderWorkspace = /\/commerce\/orders(?:\/|$)/.test(pathname);
  const hideChatFab = pathname.includes('/ai/chat') || isOrderWorkspace;

  useEffect(() => {
    Sentry.setContext('owner_navigation', {
      requested_route: navigation.location?.pathname ?? null,
      committed_route: pathname,
      state: navigation.state,
      pwa: window.matchMedia('(display-mode: standalone)').matches,
      frontend_version: __APP_VERSION__,
    });
  }, [navigation.location?.pathname, navigation.state, pathname]);

  return (
    <div
      data-app-shell="owner"
      className="flex min-h-screen flex-col bg-[#F7F7F8] dark:bg-background lg:flex-row"
    >
      {isNavigating ? (
        <div
          role="status"
          aria-label="Chargement de la page"
          className="fixed inset-x-0 top-0 z-[100] h-1 overflow-hidden bg-lime-100 dark:bg-charcoal-800"
        >
          <div className="h-full w-2/3 animate-pulse rounded-r-full bg-lime-500" />
        </div>
      ) : null}
      {!shopRevision && <VelkoSplash />}
      <CommandPalette />
      <OrderNotificationMonitor />
      <OfflineNotice />
      <PwaUpdatePrompt />
      <AppDownloadModal />
      <ReconversionScreen />
      <GracePeriodSheet />
      <FeaturePaywall />
      <DashboardV3Sidebar />
      <RailSubnavPanel />
      {!isOrderWorkspace && <MobileRailDrawer />}
      <div className="flex flex-1 flex-col min-w-0">
        <FloatingTopActions />
        {!isOrderWorkspace && <FloatingTopActionsMobile />}
        <AnnouncementBanner />
        <ExpiryCountdownBanner />
        <AccountStatusBanner />
        <main
          className={cn(
            'flex flex-1 flex-col p-4 pt-[calc(env(safe-area-inset-top,0px)+4.75rem)] pb-[calc(var(--bottom-tab-height)+var(--bottom-tab-safe-area)+1rem)] sm:p-6 sm:pt-[calc(env(safe-area-inset-top,0px)+4.75rem)] sm:pb-[calc(var(--bottom-tab-height)+var(--bottom-tab-safe-area)+1.5rem)] lg:pt-20 lg:pb-6',
            isOrderWorkspace && '!px-0 !pt-0 sm:!px-0 sm:!pt-0 lg:!px-6 lg:!pt-20',
          )}
        >
          {!isOrderWorkspace && <RailSubnavCollapsedPill />}
          <div className="mx-auto w-full max-w-screen-2xl">
            <PageTransition>
              <Outlet />
            </PageTransition>
            <ScrollRestoration />
          </div>
        </main>
        <StatusBar />
      </div>
      <BottomTabBar />
      <PwaInstallBanner />
      {!hideChatFab && <ChatFab />}
      {!isSwitching && <ProductTourHost />}
    </div>
  );
}

export default function CockpitLayout() {
  return (
    <SubNavProvider>
      <CockpitLayoutInner />
    </SubNavProvider>
  );
}
